// eslint configured: rule not present in CRA build
import { useEffect, useState } from "react";
import { Plus, Trash2, Target } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiMutate, client, PLATFORMS } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { markCoachStep } from "@/lib/coach";
import { requestNotifPermission, alreadyAsked, notifPermissionState } from "@/lib/notify";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";

function thisWeek() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}
function thisMonth() {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
  return [start, end];
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => setGoals(await apiGet("/goals"));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    await client.delete(`/goals/${id}`);
    load();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> v. The Goals</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            Your <span className="font-display-italic">rhythm</span>, your terms.
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="goals-new-btn">
          <Plus size={14} /> New goal
        </button>
      </div>

      {goals.length === 0 ? (
        <EmptyState
          testid="goals-empty"
          icon={Target}
          eyebrow="The Goals"
          title="Set the rhythm,"
          titleItalic="trust the work."
          body="A weekly target, a monthly milestone, or a quiet custom number. PintaFlow auto-tracks every publish."
          primaryLabel="Create your first goal"
          onPrimary={() => setShowNew(true)}
        />
      ) : (
        <div className="space-y-3" data-testid="goals-list">
          {goals.map((g) => {
            const pct = Math.min(100, Math.round(((g.progress || 0) / g.target) * 100));
            return (
              <div key={g.id} className="card flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="tag-brass">{g.kind}</span>
                    <span className="tag">{g.platform === "all" ? "any platform" : g.platform}</span>
                  </div>
                  <div className="mt-3" style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'wght' 450", fontSize: "1.15rem" }}>
                    {g.label}
                  </div>
                  <div className="text-xs mt-1" style={{ color: INK_55 }}>{g.period_start} → {g.period_end}</div>
                  <div className="h-1 rounded-full overflow-hidden mt-3" style={{ background: "var(--ink-22)" }}>
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: SEAL }} />
                  </div>
                  <div className="text-xs mt-1.5" style={{ color: INK_55 }}>{g.progress || 0}/{g.target} {g.unit}</div>
                </div>
                <button onClick={() => del(g.id)} className="btn-ghost" data-testid={`goal-delete-${g.id}`} style={{ color: "#c97a6a" }}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NewGoalModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewGoalModal({ onClose, onSaved }) {
  const [kind, setKind] = useState("monthly");
  const [platform, setPlatform] = useState("all");
  const [target, setTarget] = useState(4);
  const [unit, setUnit] = useState("posts");
  const [label, setLabel] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (kind === "weekly") { const [s, e] = thisWeek(); setPeriodStart(s); setPeriodEnd(e); }
    else if (kind === "monthly") { const [s, e] = thisMonth(); setPeriodStart(s); setPeriodEnd(e); }
  }, [kind]);

  const save = async () => {
    const p = PLATFORMS.find((x) => x.id === platform);
    const lbl = label.trim() || `${target} ${p ? p.label : platform}/${kind === "weekly" ? "week" : kind === "monthly" ? "month" : "period"}`;
    setSaving(true);
    try {
      await apiMutate("post", "/goals", { kind, platform, target: Number(target), unit, label: lbl, period_start: periodStart, period_end: periodEnd });
      markCoachStep("set-goal");

      // Setting a goal is the ideal moment to ask for notification permission.
      // We only ask once — if the user already chose, we respect that.
      if (!alreadyAsked() && notifPermissionState() === "default") {
        // Small delay so the modal close animation finishes first.
        setTimeout(async () => {
          const res = await requestNotifPermission("first-goal");
          if (res === "granted") {
            toast.success("Reminders on", { description: "We'll nudge you only when your streak or goal needs you." });
          }
        }, 600);
      }
      onSaved?.();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose}>
      <div className="rounded-t-2xl md:rounded-2xl w-full md:max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="goal-modal" style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}>
        <h3 className="font-heading text-2xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>New Goal</h3>

        <div>
          <label className="eyebrow block mb-2">Kind</label>
          <div className="grid grid-cols-3 gap-2">
            {["weekly", "monthly", "custom"].map((k) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`goal-kind-${k}`}
                className="py-2 rounded-full text-xs transition"
                style={{
                  background: kind === k ? "var(--ink)" : "transparent",
                  color: kind === k ? "var(--bg)" : INK_55,
                  border: kind === k ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
                }}
              >{k}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-2">Platform</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="input" data-testid="goal-platform-select">
            <option value="all">Any</option>
            {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="eyebrow block mb-2">Target</label>
            <input type="number" min={1} value={target} onChange={(e) => setTarget(e.target.value)} className="input" data-testid="goal-target-input" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Unit</label>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} className="input" data-testid="goal-unit-input" />
          </div>
        </div>

        <div>
          <label className="eyebrow block mb-2">Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="input" placeholder="auto-generated if blank" data-testid="goal-label-input" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="eyebrow block mb-2">Period start</label>
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="input" data-testid="goal-period-start" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Period end</label>
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="input" data-testid="goal-period-end" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary" data-testid="goal-cancel-btn">Cancel</button>
          <button onClick={save} disabled={saving || !periodStart || !periodEnd} className="btn-primary" data-testid="goal-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
